import React, { useState, useCallback } from 'react';
import {
  Modal,
  Radio,
  Input,
  Button,
  notification,
  Space,
  Typography,
} from 'antd';
import { CheckOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { StrategyGuideStatuses } from '@aims/shared/strategy-guides';
import { reviewStrategyGuideMutation } from './constants';

const { TextArea } = Input;
const { Text } = Typography;

function StrategyGuideReviewModal({
  visible,
  onCancel,
  onComplete,
  strategyGuide,
}) {
  const [status, setStatus] = useState(null);
  const [notes, setNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const [reviewStrategyGuide] = useMutation(reviewStrategyGuideMutation);

  const handleReview = useCallback(async () => {
    if (!status) {
      notification.error({
        message: 'Error',
        description: 'Please select a status.',
      });
      return;
    }

    try {
      setReviewing(true);
      await reviewStrategyGuide({
        variables: {
          review: {
            _id: strategyGuide._id,
            status,
            notes: notes || null,
          },
        },
      });

      notification.success({
        message: 'Success',
        description: `Strategy Guide marked as ${StrategyGuideStatuses[status]?.label}.`,
      });

      setStatus(null);
      setNotes('');
      onComplete();
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'Failed to review strategy guide.',
      });
    } finally {
      setReviewing(false);
    }
  }, [status, notes, strategyGuide, reviewStrategyGuide, onComplete]);

  const handleCancel = useCallback(() => {
    setStatus(null);
    setNotes('');
    onCancel();
  }, [onCancel]);

  if (!strategyGuide) return null;

  return (
    <Modal
      title={`Review Strategy Guide v${strategyGuide.version}`}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={reviewing}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleReview}
          loading={reviewing}
          disabled={!status}
        >
          Submit Review
        </Button>,
      ]}
      width={500}
    >
      <div style={{ marginBottom: 24 }}>
        <Text strong>Current Status: </Text>
        <Text>{StrategyGuideStatuses[strategyGuide.status]?.label}</Text>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          New Status
        </Text>
        <Radio.Group
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          buttonStyle="solid"
        >
          <Space direction="vertical">
            <Radio.Button
              value="APPROVED"
              style={{
                width: 200,
                textAlign: 'center',
              }}
            >
              <CheckOutlined style={{ marginRight: 8, color: 'green' }} />
              Approve
            </Radio.Button>
            <Radio.Button
              value="PENDING_CHANGES"
              style={{
                width: 200,
                textAlign: 'center',
              }}
            >
              <WarningOutlined style={{ marginRight: 8, color: 'orange' }} />
              Request Changes
            </Radio.Button>
            <Radio.Button
              value="DRAFT"
              style={{
                width: 200,
                textAlign: 'center',
              }}
            >
              Return to Draft
            </Radio.Button>
          </Space>
        </Radio.Group>
      </div>

      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Review Notes (Optional)
        </Text>
        <TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Add feedback or notes about this review..."
        />
        {status === 'PENDING_CHANGES' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Please provide feedback on what changes are needed for this strategy
            guide.
          </Text>
        )}
      </div>
    </Modal>
  );
}

export default StrategyGuideReviewModal;
